import AutorenewIcon from "@mui/icons-material/Autorenew";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import {Box, Button, Table, TableBody, TableCell, TableRow, useMediaQuery} from "@mui/material";
import React from "react";
import {useParams} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {levelSelector, tokenSelector} from "../../Recoil/Atom/authState";
import * as workOrderApi from "../../api/workOrderApi/workOrderApi";
import ConfirmButtonGroup from "../../components/ConfirmButtonGroup";
import MWCaption from "../../components/MWCaption";
import MWSkeleton from "../../components/MWSkeleton";
import SubTitle from "../../components/SubTitle";
import {useGetItemQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {typeValue} from "../../models/clientModels/data/selectList";
import {ResponseItem, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import {
  EQWorkOrder,
  getSerialNumber,
  statusNames,
} from "../../models/serverModels/workOrder/EQWorkOrder";
import {StyledDetailContainer, StyledDetailTable} from "../../styledComponents/StyledDetail";
import {StickyContainer} from "../../styledComponents/StyledListContainer";
import {FormContents} from "../../styledComponents/StyledCreateForm";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import {useQueryClient} from "react-query";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";

const WorkOrderDetail = () => {
  let {id} = useParams();
  const queryClient = useQueryClient();

  const tokenState = useRecoilValue(tokenSelector);
  const userLevel = useRecoilValue(levelSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const isMobile = useMediaQuery("(min-width: 960px)");

  const {
    data: workOrder,
    isLoading,
    isFetching,
  } = useGetItemQuery<ResponseItem<EQWorkOrder>>({
    key: ["workOrderDetail", id],
    api: () => workOrderApi.getItem({id: parseInt(id!), token: tokenState}),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: workOrderApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("/WorkOrder/Record");
      },
    },
  });

  return (
    <React.Fragment>
      {isMobile ? (
        <React.Fragment>
          <StickyContainer>
            <SubTitle subTitle="점검 요청 내용" />
          </StickyContainer>
          {/* 요청 */}
          <StyledDetailContainer>
            <MWCaption title={"요청"} color="#01579B" icon={<RequestPageIcon />} />
            <FormContents container rowSpacing={4}>
              <StyledDetailTable>
                <Table>
                  {isLoading && isFetching ? (
                    <TableBody>
                      <MWSkeleton row={12} column={6} />
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          일련번호
                        </TableCell>
                        <TableCell component="td" width="23.33%">
                          {getSerialNumber(workOrder?.item!)}
                        </TableCell>
                        <TableCell component="th" width="10%">
                          진행단계
                        </TableCell>
                        <TableCell component="td" width="23.33%">
                          {statusNames.find((v, index) =>
                            index === workOrder?.item?.statusIndex ? v : ""
                          )}
                        </TableCell>
                        <TableCell component="th" width="10%">
                          요청일자
                        </TableCell>
                        <TableCell component="td" width="23.33%">{`${new Date(
                          workOrder?.item?.requestDate!
                        ).getFullYear()}년 ${
                          new Date(workOrder?.item?.requestDate!).getMonth() + 1
                        }월 ${new Date(workOrder?.item?.requestDate!).getDate()}일`}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">작성자</TableCell>
                        <TableCell component="td">{workOrder?.item?.writerName}</TableCell>
                        <TableCell component="th">설비분류</TableCell>
                        <TableCell component="td">{workOrder?.item?.specification.type}</TableCell>
                        <TableCell component="th">설비번호</TableCell>
                        <TableCell component="td">
                          {workOrder?.item?.specification.identifyNumber}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">설비명</TableCell>
                        <TableCell>{workOrder?.item?.specification.name}</TableCell>
                        <TableCell component="th">구분</TableCell>
                        <TableCell>
                          {typeValue.map((v) =>
                            v.value === workOrder?.item?.checkType ? v.label : ""
                          )}
                        </TableCell>
                        <TableCell component="th">관련자료</TableCell>
                        <TableCell>{workOrder?.item?.etcFile}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">요청내용</TableCell>
                        <TableCell>{workOrder?.item?.requestContent}</TableCell>

                        <TableCell component="th">개선 전</TableCell>
                        <TableCell colSpan={3}>
                          <img
                            src={process.env.REACT_APP_PATH_WORKORDER + workOrder?.item?.preFile!}
                            alt=""
                            width="200px"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </StyledDetailTable>
            </FormContents>
          </StyledDetailContainer>
          {/* 지연 */}
          {workOrder?.item?.statusIndex === 3 && (
            <StyledDetailContainer>
              <MWCaption title={"지연"} color="#01579B" icon={<WatchLaterIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          처리부서
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.department}</TableCell>
                        <TableCell component="th" width="10%">
                          지연사유
                        </TableCell>
                        <TableCell>{workOrder?.item?.delayContent}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          {/* 반려 */}
          {workOrder?.item?.statusIndex === 2 && (
            <StyledDetailContainer>
              <MWCaption title={"반려"} color="#01579B" icon={<ThumbDownOffAltIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          처리부서
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.department}</TableCell>
                        <TableCell component="th" width="10%">
                          반려사유
                        </TableCell>
                        <TableCell>{workOrder?.item?.rejectContent}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          {/* 접수 */}
          {(workOrder?.item?.statusIndex === 1 || 4) && (
            <StyledDetailContainer>
              <MWCaption title={"접수"} color="#01579B" icon={<ThumbUpOffAltIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          처리부서
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.department}</TableCell>
                        <TableCell component="th" width="10%">
                          설비위험도
                        </TableCell>
                        <TableCell width="23.33%">
                          {workOrder?.item?.specification.eqLevel}
                        </TableCell>
                        <TableCell component="th" width="10%">
                          변경여부
                        </TableCell>
                        <TableCell width="23.33%">
                          {workOrder?.item?.isChangable ? "대상" : "비대상"}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          {/* 진행 */}
          {(workOrder?.item?.statusIndex === 1 || 4) && (
            <StyledDetailContainer>
              <MWCaption title={"진행"} color="#01579B" icon={<AutorenewIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          완료예정일자
                        </TableCell>
                        <TableCell width="23.33%">{`${new Date(
                          workOrder?.item?.estimatedDate!
                        ).getFullYear()}년 ${
                          new Date(workOrder?.item?.estimatedDate!).getMonth() + 1
                        }월 ${new Date(workOrder?.item?.estimatedDate!).getDate()}일`}</TableCell>
                        <TableCell component="th" width="10%">
                          처리담당자
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.receiverName}</TableCell>
                        <TableCell component="th" width="10%">
                          시작시간
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.startTime}시</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          {/* 완료 */}
          {workOrder?.item?.statusIndex === 4 && (
            <StyledDetailContainer>
              <MWCaption title={"완료"} color="#01579B" icon={<DoneAllIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          종료시간
                        </TableCell>
                        <TableCell width="23.33%">{`${new Date(
                          workOrder?.item?.endTime!
                        ).getFullYear()}년 ${
                          new Date(workOrder?.item?.endTime!).getMonth() + 1
                        }월 ${new Date(workOrder?.item?.endTime!).getDate()}일`}</TableCell>
                        <TableCell component="th" width="10%">
                          소요금액
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.cost}원</TableCell>
                        <TableCell component="th" width="10%">
                          가동전점검
                        </TableCell>
                        <TableCell width="23.33%">
                          {workOrder?.item?.isClosed ? "O" : "해당없음"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          개선 후
                        </TableCell>
                        <TableCell colSpan={5}>{workOrder?.item?.postFile}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          <Box
            style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "24px"}}
          >
            <ConfirmButtonGroup
              link="/WorkOrder"
              cancelText="취소"
              confirmText="편집"
              editLink={`/WorkOrder/EditManual/${workOrder?.item?.uid}`}
            />
            {userLevel === 3 && (
              <Button
                variant="contained"
                color="secondary"
                style={{margin: "40px 0 80px 0"}}
                onClick={() => {
                  setAlertState({
                    toggle: true,
                    message: "등록된 점검의뢰 요청을 삭제하시겠습니까?",
                    execution: () => removeMutate({token: tokenState, id: parseInt(id!)}),
                  });
                }}
              >
                삭제
              </Button>
            )}
          </Box>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <StickyContainer>
            <SubTitle subTitle="점검 요청 내용" />
          </StickyContainer>
          {/* 요청 */}
          <StyledDetailContainer>
            <MWCaption title={"요청"} color="#01579B" icon={<RequestPageIcon />} />
            <FormContents container rowSpacing={4}>
              <StyledDetailTable>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" width="10%">
                        일련번호
                      </TableCell>
                      <TableCell component="td" width="23.33%">
                        {getSerialNumber(workOrder?.item!)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" width="10%">
                        진행단계
                      </TableCell>
                      <TableCell component="td" width="23.33%">
                        {statusNames.find((v, index) =>
                          index === workOrder?.item?.statusIndex ? v : ""
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" width="10%">
                        요청일자
                      </TableCell>
                      <TableCell component="td" width="23.33%">{`${new Date(
                        workOrder?.item?.requestDate!
                      ).getFullYear()}년 ${
                        new Date(workOrder?.item?.requestDate!).getMonth() + 1
                      }월 ${new Date(workOrder?.item?.requestDate!).getDate()}일`}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">작성자</TableCell>
                      <TableCell>{workOrder?.item?.writerName}</TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell component="th">설비분류</TableCell>
                      <TableCell width="23.33%">{workOrder?.item?.specification.type}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">설비번호</TableCell>
                      <TableCell>{workOrder?.item?.specification.identifyNumber}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">설비명</TableCell>
                      <TableCell>{workOrder?.item?.specification.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">구분</TableCell>
                      <TableCell>
                        {typeValue.map((v) =>
                          v.value === workOrder?.item?.checkType ? v.label : ""
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">관련자료</TableCell>
                      <TableCell>{workOrder?.item?.etcFile}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">요청내용</TableCell>
                      <TableCell>{workOrder?.item?.requestContent}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">개선 전</TableCell>
                      <TableCell colSpan={3}>
                        <img
                          src={process.env.REACT_APP_PATH_WORKORDER + workOrder?.item?.preFile!}
                          alt=""
                          width="100px"
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </StyledDetailTable>
            </FormContents>
          </StyledDetailContainer>
          {/* 지연 */}
          {workOrder?.item?.statusIndex === 3 && (
            <StyledDetailContainer>
              <MWCaption title={"지연"} color="#01579B" icon={<WatchLaterIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          처리부서
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.department}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">지연사유</TableCell>
                        <TableCell>{workOrder?.item?.delayContent}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          {/* 반려 */}
          {workOrder?.item?.statusIndex === 2 && (
            <StyledDetailContainer>
              <MWCaption title={"반려"} color="#01579B" icon={<ThumbDownOffAltIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          처리부서
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.department}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">반려사유</TableCell>
                        <TableCell>{workOrder?.item?.rejectContent}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          {/* 접수 */}
          {(workOrder?.item?.statusIndex === 1 || 4) && (
            <StyledDetailContainer>
              <MWCaption title={"접수"} color="#01579B" icon={<ThumbUpOffAltIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          처리부서
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.department}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          설비위험도
                        </TableCell>
                        <TableCell width="23.33%">
                          {workOrder?.item?.specification.eqLevel}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          변경여부
                        </TableCell>
                        <TableCell width="23.33%">
                          {workOrder?.item?.isChangable ? "대상" : "비대상"}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          {/* 진행 */}
          {(workOrder?.item?.statusIndex === 1 || 4) && (
            <StyledDetailContainer>
              <MWCaption title={"진행"} color="#01579B" icon={<AutorenewIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          완료예정일자
                        </TableCell>
                        <TableCell width="23.33%">{`${new Date(
                          workOrder?.item?.estimatedDate!
                        ).getFullYear()}년 ${
                          new Date(workOrder?.item?.estimatedDate!).getMonth() + 1
                        }월 ${new Date(workOrder?.item?.estimatedDate!).getDate()}일`}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          처리담당자
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.receiverName}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          시작시간
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.startTime}시</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          {/* 완료 */}
          {workOrder?.item?.statusIndex === 4 && (
            <StyledDetailContainer>
              <MWCaption title={"완료"} color="#01579B" icon={<DoneAllIcon />} />
              <FormContents container rowSpacing={4}>
                <StyledDetailTable>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          종료시간
                        </TableCell>
                        <TableCell width="23.33%">{`${new Date(
                          workOrder?.item?.endTime!
                        ).getFullYear()}년 ${
                          new Date(workOrder?.item?.endTime!).getMonth() + 1
                        }월 ${new Date(workOrder?.item?.endTime!).getDate()}일`}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          소요금액
                        </TableCell>
                        <TableCell width="23.33%">{workOrder?.item?.cost}원</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          가동전점검
                        </TableCell>
                        <TableCell width="23.33%">
                          {workOrder?.item?.isClosed ? "O" : "해당없음"}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" width="10%">
                          개선 후
                        </TableCell>
                        <TableCell colSpan={5}>{workOrder?.item?.postFile}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledDetailTable>
              </FormContents>
            </StyledDetailContainer>
          )}
          <Box
            style={{display: "flex", alignItems: "center", justifyContent: "center", gap: "24px"}}
          >
            <ConfirmButtonGroup
              link="/WorkOrder"
              cancelText="취소"
              confirmText="편집"
              editLink={`/WorkOrder/EditManual/${workOrder?.item?.uid}`}
            />
            {userLevel === 3 && (
              <Button
                variant="contained"
                color="secondary"
                style={{margin: "40px 0 80px 0"}}
                onClick={() => {
                  setAlertState({
                    toggle: true,
                    message: "등록된 점검의뢰 요청을 삭제하시겠습니까?",
                    execution: () => removeMutate({token: tokenState, id: parseInt(id!)}),
                  });
                }}
              >
                삭제
              </Button>
            )}
          </Box>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default WorkOrderDetail;
